// Basis imports die altijd nodig zijn (vooraan voor overzicht)
import Swup from 'swup';
import { reInitScripts } from '../app.js';
import { router } from 'js-dom-router';
import { destroyCursor } from '../inc/custom-cursor.js';
import { closeMenu } from '../inc/nav.js';
import { gsap } from 'gsap'; // moet condiitioneel

/**
 * Eenvoudige Loader-klasse voor Swup.js
 */
class Loader {
  constructor(options = {}) {
    // Standaard opties
    this.options = {
      // Swup-instellingen
      containerSelector: '#main',
      //animationSelector: '[class*="transition-"]',
      scrollToTop: true,

      // Animatie-opties
      animationType: 'css',

      fadeOutDuration: 0,  
      fadeInDuration: 0,  

      // Indicator-opties
      showLoadingIndicator: false,
      mainClass: 'transition-fade',

      // Formulieren
      handleForms: true,

      // Plugin-booleans (alles uit/aan te zetten)
      plugins: {
        fragment: true,
        bodyClass: true,
        a11y: true,
        head: true,
        scripts: true,
        preload: true,
        forms: true,   
        js: false,
        scroll: true      
      },

      // Optionele fragment-plugin-regels (verplicht voor gebruik)
      fragmentRules: [
        //   from: '/werk/:filter?',
        //   to: '/werk/:filter?',
        //   containers: ['[data-ref="posts"]']
        // {
        //   from: /\/werk/, // Van werk overzicht
        //   to: /\/projects\/.*/, // Naar project detail
        //   containers: ['#project-content'], // Alleen content updaten, niet de hele pagina
        // },
        {
          // Filter-functionaliteit blijft zoals het was
          from: /.*/,
          to: /.*/,
          containers: ['#projects', '#filter-container'],
          if: (visit) => {
              // Kijk of er een ?filter= in de querystring zit
              return new URL(visit.to.url, window.location.origin).searchParams.has('filter');
          }
        },
        {
            // Extra fragment-regel voor paginatie (page/*)
            from: /.*/,
            to: /.*/,
            containers: ['[data-swup-container="filter"]', '#projects', '.pagination'],
            if: (visit) => {
                // Kijk of er een paginatie-parameter in de URL staat (bijv. /page/2/)
                return new URL(visit.to.url, window.location.origin).pathname.match(/page\/\d+/);
            }
        },
      ],

      ...options
    };

    this.swup = null;
    this.plugins = [];
    this.initialized = false;
  }

  /**
   * Initialiseer alle functionaliteit
   */
  async init() {

    // Disable automatic scroll restoration by the browser
    if (history.scrollRestoration) {
      history.scrollRestoration = 'manual';
    }
    
    if (this.initialized) return;

    // Voeg main class toe voor CSS-animatie
    const mainEl = document.querySelector(this.options.containerSelector);
    if (mainEl && !mainEl.classList.contains(this.options.mainClass)) {
      mainEl.classList.add(this.options.mainClass);
    }

    // (Optioneel) loading-indicator
    this.handleLoadingIndicator();

    // Laad plugin-instanties
    await this.collectPlugins();

    // Initialiseer Swup
    this.bootstrapSwup();
    this.initialized = true;

    // Formulieren bij eerste load markeren
    if (this.options.plugins.forms) this.markForms();

    return this; // chaining
  }

  /**
   * Laad alle plugins op basis van opties
   */
  async collectPlugins() {
    const toLoad = this.options.plugins;
    const tasks = [];

    // Doorloop alle plugin-booleans
    for (const [pluginName, active] of Object.entries(toLoad)) {
      if (active) {
        tasks.push(this.loadPlugin(pluginName));
      }
    }

    await Promise.all(tasks);
  }

  /**
   * Laad specifieke plugin via dynamic import
   */
  async loadPlugin(plugin) {
    try {
      switch (plugin) {
        
        case 'fragment': {
            const { default: SwupFragmentPlugin } = await import('@swup/fragment-plugin');
            this.plugins.push(
                new SwupFragmentPlugin({
                    rules: this.options.fragmentRules,
                    debug: true 
                })
            );
            break;
        }
        case 'bodyClass': {
          const { default: SwupBodyClassPlugin } = await import('@swup/body-class-plugin');
          this.plugins.push(new SwupBodyClassPlugin());
          break;
        }
        case 'a11y': {
          const { default: SwupA11yPlugin } = await import('@swup/a11y-plugin');
          this.plugins.push(
            new SwupA11yPlugin({
              headingSelector: 'h1, .page-title',
              respectReducedMotion: true
              // eventueel announcements hier
            })
          );
          break;
        }
        case 'head': {
          const { default: SwupHeadPlugin } = await import('@swup/head-plugin');
          this.plugins.push(new SwupHeadPlugin({ persistAssets: true, awaitAssets: false }));
          break;
        }
        case 'scripts': {
          const { default: SwupScriptsPlugin } = await import('@swup/scripts-plugin');
          this.plugins.push(new SwupScriptsPlugin({ head: true, body: true }));
          break;
        }
        case 'preload': {
          const { default: SwupPreloadPlugin } = await import('@swup/preload-plugin');
          this.plugins.push(new SwupPreloadPlugin());
          break;
        }
        case 'forms': {
          const { default: SwupFormsPlugin } = await import('@swup/forms-plugin');
          this.plugins.push(
            new SwupFormsPlugin({
              formSelector: 'form[data-swup-form]',
              stripEmptyParams: true
            })
          );
          break;
        }
        // case 'scroll': {
        //   const { default: SwupScrollPlugin } = await import('@swup/scroll-plugin');
        //   this.plugins.push(new SwupScrollPlugin());
        //   //console.log("✅ SwupScrollPlugin geladen!");
        //   break;
        // }

        case 'scroll': {
          const { default: SwupScrollPlugin } = await import('@swup/scroll-plugin');
          this.plugins.push(new SwupScrollPlugin({
            doScrollingRightAway: false, // Delay scrolling until layout is stabilized
            animateScroll: {
              betweenPages: true,
              samePageWithHash: true,
              samePage: true
            },
            // Custom offset to account for a fixed header; adjust selector if needed
            //offset: () => document.querySelector('#header') ? document.querySelector('#header').offsetHeight : 0
            offset: 0,
          }));
          break;
        }
      
        case 'js': {
          // Laad GSAP en JS-plugin
          // Not used
          const [gsapModule, jsPluginModule] = await Promise.all([
            import('gsap'),
            import('@swup/js-plugin')
          ]);
          const { gsap } = gsapModule;
          const SwupJsPlugin = jsPluginModule.default;

          this.plugins.push(
            new SwupJsPlugin({
              animations: [
                {
                  from: '(.*)',
                  to: '(.*)',
                  out: () => {
                    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
                    return gsap.to(this.options.containerSelector, {
                      opacity: 0,
                      duration: this.options.fadeOutDuration,
                      ease: 'power1.out'
                    });
                  },
                  in: () => {
                    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
                    return gsap.fromTo(
                      this.options.containerSelector,
                      { opacity: 0 },
                      {
                        opacity: 1,
                        duration: this.options.fadeInDuration,
                        ease: 'power2.out'
                      }
                    );
                  }
                }
              ]
            })
          );
          break;
        }
      }
    } catch (error) {
      console.error(`Fout bij laden van plugin "${plugin}":`, error);
      // Fallback naar CSS als JS-animaties falen
      if (plugin === 'js') this.options.animationType = 'css';
    }
  }

  /**
   * Initialiseer Swup.js met de verzamelde plugins
   */
  bootstrapSwup() {
    if (!document.querySelector(this.options.containerSelector)) {
      console.error(
        `Kan de Swup-container niet vinden: ${this.options.containerSelector}`
      );
      return;
    }

    this.swup = new Swup({
      containers: [this.options.containerSelector, '.main-header', '.mobile-nav-layer', '#menu-mobile'],
      animationSelector: this.options.animationSelector,
      cache: true,
      linkSelector:
        'a:not(.prevent):not([target="_blank"]):not([href^="#"]):not([href^="/wp-admin/"]):not([href^="/wp-login.php"])',
      plugins: this.plugins
    });

    // Expose instance for component modules that need to orchestrate VT around navigate
    try { window.swup = this.swup; } catch(e) {}

    this.setupHooks();
  }

  /**
   * Event hooks registreren
   */
  setupHooks() {
    // Voor form plugin
    if (this.options.plugins.forms && this.swup.hooks) {
      this.swup.hooks.on('form:submit', () => {
        // console.log('Form submitted via Swup');
      });
    }

    if (!this.swup.hooks) return; // Swup4 hooks-check

    // Content vervangen
    this.swup.hooks.on('content:replace', (visit) => {
        console.log('🔄 Fragment update uitgevoerd:', visit.containers);
       

        // Replace manual scroll with swup's scrollTo method to use plugin options
        // Maar NIET tijdens onze custom shared-element transitie
        if (!document.documentElement.classList.contains('awaiting-custom-transition') && this.options.scrollToTop && !window.location.hash) {
          if (this.swup && typeof this.swup.scrollTo === 'function') {
            // Use swup.scrollTo to animate the scroll position to the top
            this.swup.scrollTo(0, true);
          } else {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
          }
        }

        if (typeof pagescripts?.init === 'function') {
            //pagescripts.init();
        }
        if (this.options.plugins.forms) {
            this.markForms();
        }
    });

        // Project thumbnail transitie met Swup lifecycle
    let transitionClone = null;

    // Project transition logic in bestaande visit:start hook wordt geïntegreerd

    // Timingcontrole gebeurt via tijdelijk verbergen/tonen van containers

    // Start de inkomende weergave pas nadat onze clone animatie klaar is
    this.swup.hooks.on('content:replace', (visit) => {
      if (!transitionClone) return;

      // Verberg nieuwe content totdat animatie klaar is (zonder fade)
      const newMain = document.querySelector('#main');
      if (newMain) {
        newMain.style.visibility = 'hidden';
        newMain.style.opacity = '';
        newMain.style.transition = '';
        newMain.style.willChange = '';
      }

      // Normaliseer scroll zodat hero-positie altijd correct tov viewport wordt gemeten
      try { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); } catch(e) {}

      requestAnimationFrame(() => {
      // Zoek de echte hero op de nieuwe pagina (voorkeur: figure i.p.v. img)
      let hero = document.querySelector('.project-detail-hero');
      if (hero) {
        hero = hero.closest('figure') || hero;
        const heroRect = hero.getBoundingClientRect();

          console.log('🎯 Animating to real hero position:', {
            top: heroRect.top,
            left: heroRect.left,
            width: heroRect.width,
            height: heroRect.height
          });

          // Animeer naar echte hero positie
          transitionClone.style.top = `${heroRect.top}px`;
          transitionClone.style.left = `${heroRect.left}px`;
          transitionClone.style.width = `${heroRect.width}px`;
          transitionClone.style.height = `${heroRect.height}px`;
        // Match de doel rounding van de hero/figure
        try {
          const targetRadius = getComputedStyle(hero).borderRadius || '0px';
          transitionClone.style.borderRadius = targetRadius;
        } catch (e) {
          transitionClone.style.borderRadius = '0px';
        }

          // Wacht tot animatie klaar is, dan verwijder clone en toon nieuwe content
          setTimeout(() => {
            if (transitionClone) {
              transitionClone.style.visibility = 'hidden';
              setTimeout(() => {
                if (transitionClone) {
                  transitionClone.remove();
                  transitionClone = null;
                }
                // Toon nieuwe content
                if (newMain) newMain.style.visibility = 'visible';
                document.documentElement.classList.remove('awaiting-custom-transition');
                console.log('🎉 Custom transition complete, revealing new page');
              }, 300);
            }
          }, 800);
        } else {
          // Geen hero gevonden, cleanup direct en toon nieuwe content
          if (transitionClone) {
            transitionClone.remove();
            transitionClone = null;
          }
          if (newMain) newMain.style.opacity = '1';
          document.documentElement.classList.remove('awaiting-custom-transition');
        }
      });
    });

    // Link click hook - voor project transition logic
    this.swup.hooks.on('link:click', (visit, { el }) => {
      // Project transition logic
      if (el) {
        // Zoek de figure als parent voor juiste aspect/rounding
        let thumbnail = el.querySelector('.project-thumbnail');
        let figure = thumbnail ? thumbnail.closest('figure') : null;
        const sourceEl = figure || thumbnail;
        
        if (sourceEl) {
          const isProjectLink = visit.to.url.includes('/projects/') || visit.to.url.includes('/project/');
          if (isProjectLink) {
            console.log('🎬 Starting project transition');
            
            // Clone de figure (of fallback: image)
            transitionClone = sourceEl.cloneNode(true);
            const rect = sourceEl.getBoundingClientRect();
            
            transitionClone.style.cssText = `
              position: fixed;
              top: ${rect.top}px;
              left: ${rect.left}px;
              width: ${rect.width}px;
              height: ${rect.height}px;
              z-index: 9999;
              border-radius: ${getComputedStyle(sourceEl).borderRadius || '1rem'};
              overflow: hidden;
              transition: all 0.8s ease-out;
              pointer-events: none;
            `;
            
            document.body.appendChild(transitionClone);

            // Verberg toekomstige content meteen via HTML class om flicker te voorkomen
            document.documentElement.classList.add('awaiting-custom-transition');

            // (Optioneel) fade huidige main iets uit
            const currentMain = document.querySelector('#main');
            if (currentMain) {
              currentMain.style.transition = 'opacity 200ms ease';
              currentMain.style.opacity = '0.3';
            }
          }
        }
      }
    });

    // Bezoek start
    this.swup.hooks.on('visit:start', (visit) => {
      // Normale visit:start logic
      closeMenu();
      this.cleanupIntersectionObservers();
      document.dispatchEvent(new Event('swup:before-leave'));
    });

    // Bezoek eind
    this.swup.hooks.on('visit:end', () => {
        // console.log('✅ Pagina bezoek voltooid');
      if (typeof reInitScripts === 'function') reInitScripts();
      document.body.dispatchEvent(new Event('post-load'));
      document.dispatchEvent(new Event('app'));
      if (typeof router?.ready === 'function') router.ready();

      // jQuery events (compat)
      if (typeof jQuery !== 'undefined') {
        jQuery(document).trigger('ready');
        jQuery(window).trigger('load');
      }
      document.dispatchEvent(new Event('swup:done'));
    });

  }

  /**
   * Loading indicator handler
   */
  handleLoadingIndicator() {
    const existing = document.querySelector('.loading-indicator');
    if (existing) existing.remove();

    if (this.options.showLoadingIndicator) {
      const indicator = document.createElement('div');
      indicator.className = 'loading-indicator';
      document.body.appendChild(indicator);
    } else {
      document.documentElement.classList.add('no-loading-indicator');
    }
  }

  /**
   * Markeer formulieren voor Swup
   */
  markForms() {
    // Contact Form 7
    document.querySelectorAll('.wpcf7 form').forEach((form) => {
      if (!form.hasAttribute('data-swup-form')) {
        form.setAttribute('data-swup-form', '');
      }
    });
    // Gravity Forms
    document.querySelectorAll('.gform_wrapper form').forEach((form) => {
      if (!form.hasAttribute('data-swup-form')) {
        form.setAttribute('data-swup-form', '');
      }
    });
  }

  /**
   * Voorkom memory leaks door IntersectionObservers te ontkoppelen
   */
  cleanupIntersectionObservers() {
    if (!window.IntersectionObserver) return;
    const active = new Set();
    const originalObserve = IntersectionObserver.prototype.observe;
    IntersectionObserver.prototype.observe = function (el) {
      active.add(this);
      return originalObserve.call(this, el);
    };
    active.forEach((obs) => obs.disconnect());
  }
}

/**
 * Eventueel page-specific scripts
 */
const pagescripts = {
  init() {
    if (document.body.classList.contains('page')) this.page();
    else if (document.body.classList.contains('archive')) this.archive();
    else if (document.body.classList.contains('single')) this.single();
    else if (document.body.classList.contains('home')) this.home();
    this.common();
  },
  common() {},
  page() {},
  archive() {},
  single() {},
  home() {}
};

export default Loader;
